// Territory map — where a dealmaker's book of companies is concentrated,
// painted on a US state choropleth. "Book" = companies employing at least one
// contact owned by the dealmaker (same definition as the follow-up priority
// lists). Rendered as an Analytics mode ("Map"). Data: /api/territory/*.
// Fills use color-mix() off --accent/--surface so theme + accent switching
// work with no chart-specific palette (see charts/chart_renderer.jsx:1-13 for
// the no-charting-lib rationale; the state outlines live in charts/us_states.jsx).

// Spinner keyframes for the research-revenue button (idempotent inject,
// same pattern as primitives.jsx's shimmer).
const __T_SPIN_STYLE_ID = 'atlas-territory-spin';
if (typeof document !== 'undefined' && !document.getElementById(__T_SPIN_STYLE_ID)) {
  const el = document.createElement('style');
  el.id = __T_SPIN_STYLE_ID;
  el.textContent = '@keyframes atlas-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(el);
}

const T_METRICS = [
  { key: 'companies', label: 'Companies', help: 'Distinct companies in the state' },
  { key: 'interactions', label: 'Interactions', help: 'Sum of note touchpoints across those companies' },
  { key: 'revenue', label: 'Revenue', help: 'Sum of revenue estimates — where the dollars concentrate, not the headcount' },
];

// Metric-aware number formatting (revenue reads as money everywhere).
const tFmtMetric = (metric, v) =>
  (metric === 'revenue' ? window.fmtMoney(v || 0) : (v == null ? '—' : Number(v).toLocaleString()));

// Filter picklists. Touches 1+ is the default cut — contact ownership covers
// the bulk-assigned prospect universe, so "Any" shows tens of thousands.
const T_TOUCH_OPTS = [
  { value: '0', label: 'Touches: any' },
  { value: '1', label: 'Touches: 1+' },
  { value: '5', label: 'Touches: 5+' },
  { value: '10', label: 'Touches: 10+' },
  { value: '25', label: 'Touches: 25+' },
];
const T_EBITDA_OPTS = [
  { value: '', label: 'EBITDA: any' },
  { value: '500000', label: 'EBITDA ≥ $500K' },
  { value: '1000000', label: 'EBITDA ≥ $1M' },
  { value: '2000000', label: 'EBITDA ≥ $2M' },
  { value: '5000000', label: 'EBITDA ≥ $5M' },
];
const T_ENGAGEMENT_OPTS = [
  { value: 'any', label: 'Engagements: any' },
  { value: 'none', label: 'No engagement' },
  { value: 'has', label: 'Has engagement' },
];
// The data-discovery cut: the book mixes real prospects with the service
// providers / referral partners / PE buyers who employ contacts we own.
// Sellers-only is the default — that's who this map exists to find.
const T_TYPE_OPTS = [
  { value: 'sellers', label: 'Type: sellers', hint: 'business owners we could represent' },
  { value: 'any', label: 'Type: any company' },
  { value: 'buyers', label: 'Type: buyers', hint: 'PE groups / platform buyers' },
  { value: 'service_providers', label: 'Type: service providers', hint: 'advisors, partners, referral sources' },
];
const T_REV_MIN_OPTS = [
  { value: '', label: 'Rev ≥ any' },
  { value: '1000000', label: 'Rev ≥ $1M' },
  { value: '3000000', label: 'Rev ≥ $3M' },
  { value: '5000000', label: 'Rev ≥ $5M' },
  { value: '10000000', label: 'Rev ≥ $10M' },
  { value: '25000000', label: 'Rev ≥ $25M' },
];
const T_REV_MAX_OPTS = [
  { value: '', label: 'Rev ≤ any' },
  { value: '5000000', label: 'Rev ≤ $5M' },
  { value: '10000000', label: 'Rev ≤ $10M' },
  { value: '25000000', label: 'Rev ≤ $25M' },
  { value: '50000000', label: 'Rev ≤ $50M' },
];

const tFmtInt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Sub-linear ramp so CA/TX don't flatten every other state to near-zero.
const tFill = (v, max) => {
  if (!v) return 'var(--surface-2)';
  const t = Math.sqrt(v / Math.max(max, 1));
  const pct = Math.round(12 + t * 78);
  return `color-mix(in oklab, var(--accent) ${pct}%, var(--surface))`;
};

// Drill-down drawer: the ranked companies behind one state (or the
// "no state" bucket). Mirrors TPDrill in team_pulse.jsx.
const T_DRILL_LIMIT = 200;  // server cap — flagged in the export menu when hit

const T_EXPORT_COLS = [
  { key: 'name', label: 'Company' },
  { key: 'hq_city', label: 'City' },
  { key: 'hq_state', label: 'State' },
  { key: 'revenue_est', label: 'Revenue Estimate' },
  { key: 'ebitda_est', label: 'EBITDA Estimate' },
  { key: 'interaction_count', label: 'Interactions' },
  { key: 'last_interaction_at', label: 'Last Interaction' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'id', label: 'Company ID' },
];

// AI address enrichment — lives in the "no state" drawer, where the gap is
// visible. One job at a time server-side; the panel resumes a running job on
// mount so reopening the drawer keeps showing progress. Enriched companies
// join the map/dots/radar automatically (territory reads the enriched
// fallback columns) once the job finishes and drops the server cache.
const TEnrichPanel = ({ minInteractions, requestedBy }) => {
  const [job, setJob] = React.useState(null);
  const [err, setErr] = React.useState('');
  const live = job && (job.status === 'running' || job.status === 'queued');
  React.useEffect(() => {
    window.AtlasApi.geoEnrichJobs()
      .then((rows) => {
        const j = (rows || []).find((x) => x.status === 'running' || x.status === 'queued');
        if (j) setJob(j);
      })
      .catch(() => {});
  }, []);
  // Enrichment runs for a while (250 records, one web search each), so poll
  // from 3s and let the ladder climb; the signature is the status rather than
  // the job blob, whose progress counters move on every record.
  const jobId = job && job.id;
  window.usePolling(
    React.useCallback(() => (
      window.AtlasApi.geoEnrichJob(jobId).then((j) => { setJob(j); return j && j.status; }).catch(() => undefined)
    ), [jobId]),
    { active: !!live, key: jobId, first: 3000, signature: (status) => status },
  );
  const start = () => {
    setErr('');
    window.AtlasApi.geoEnrichStart({
      max_records: 250, cost_cap_usd: 5,
      min_interactions: minInteractions, only_missing_state: true,
      requested_by: requestedBy || null,
    }).then(setJob).catch((e) => setErr((e && (e.message || e.detail)) || 'Failed to start'));
  };
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!live && (
          <window.Btn size="xs" variant="accent" onClick={start}>
            {job && job.status === 'completed' ? 'Enrich 250 more (AI)' : 'Enrich with AI (web search)'}
          </window.Btn>
        )}
        {live && (
          <>
            <span style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
              Enriching… {tFmtInt(job.total_processed)} done
              {job.total_enriched > 0 ? ` · ${tFmtInt(job.total_enriched)} found` : ''}
              {job.total_failed > 0 ? ` · ${tFmtInt(job.total_failed)} failed` : ''}
            </span>
            <window.Btn size="xs" variant="ghost"
              onClick={() => window.AtlasApi.geoEnrichCancel(job.id).then(setJob).catch(() => {})}>
              Stop
            </window.Btn>
          </>
        )}
        {!live && job && job.status === 'completed' && (
          <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            Last run: {tFmtInt(job.total_enriched)} of {tFmtInt(job.total_processed)} located
            — reload the map to see them placed
          </span>
        )}
      </div>
      {err && <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--rose-fg)' }}>{err}</div>}
    </div>
  );
};

const TerritoryDrill = ({ drill, dealmakerId, dealmakerName, activeUserName, filters, onClose }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [byCity, setByCity] = React.useState(false);
  React.useEffect(() => {
    if (!drill) return;
    let on = true;
    setData(null); setErr('');
    window.AtlasApi.territoryCompanies({
      dealmaker_id: dealmakerId, region: drill.region, limit: T_DRILL_LIMIT, ...filters,
    })
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr((e && (e.message || e.detail)) || 'Failed to load'); });
    return () => { on = false; };
  }, [drill, dealmakerId, filters]);
  // City grouping: biggest cluster first, priority order preserved inside.
  // (Hooks stay above the null return — conditional hooks crash React.)
  const groups = React.useMemo(() => {
    if (!data || !byCity) return null;
    const m = new Map();
    data.items.forEach((it) => {
      const k = (it.hq_city || '').trim() || '(no city)';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    });
    return [...m.entries()].sort((a, b) =>
      (b[1].length - a[1].length) || a[0].localeCompare(b[0]));
  }, [data, byCity]);
  if (!drill) return null;
  const title = drill.region === 'none' ? 'No state on record' : drill.region;

  const doExport = (fmt) => {
    const slug = (title || 'region').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const who = (dealmakerName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const base = `territory-${who ? who + '-' : ''}${slug}`;
    const rows = (byCity && groups ? groups.flatMap(([, its]) => its) : data.items)
      .map((it) => ({ ...it, contacts: (it.contact_names || []).join('; ') }));
    if (fmt === 'json') window.exportJson(`${base}.json`, rows);
    else window.exportCsv(`${base}.csv`, T_EXPORT_COLS, rows);
  };
  return (
    <window.Drawer open onClose={onClose} width={560}
      title={title}
      subtitle={`${tFmtInt(drill.companies)} companies · ranked by interactions, then revenue`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'inline-flex', padding: 2, background: 'var(--surface-2)',
          border: '1px solid var(--border)', borderRadius: 7 }}>
          {[[false, 'By priority'], [true, 'By city']].map(([v, l]) => (
            <button key={l} onClick={() => setByCity(v)} style={{
              padding: '3px 10px', border: 'none', cursor: 'pointer', borderRadius: 5, fontSize: 11.5,
              fontWeight: byCity === v ? 600 : 500,
              background: byCity === v ? 'var(--surface)' : 'transparent',
              color: byCity === v ? 'var(--text)' : 'var(--text-3)',
              boxShadow: byCity === v ? 'var(--shadow-sm)' : 'none',
            }}>{l}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <window.ExportMenu disabled={!data || data.items.length === 0}
          viewCount={data ? data.items.length : null}
          capNote={data && data.items.length >= T_DRILL_LIMIT
            ? `first ${T_DRILL_LIMIT} by interactions` : null}
          onExport={doExport} />
      </div>
      {drill.topCities && drill.topCities.length > 0 && (
        <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--text-3)',
          borderBottom: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
          {drill.topCities.map((c) => (
            <span key={c.city}><span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{c.city}</span> {c.n}</span>
          ))}
        </div>
      )}
      {drill.region === 'none' && (
        <TEnrichPanel minInteractions={filters.min_interactions || 0}
          requestedBy={activeUserName} />
      )}
      {err && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!data && !err && <div style={{ padding: 14 }}><window.SkeletonRows rows={5} /></div>}
      {data && (
        <div style={{ padding: '6px 0' }}>
          {data.items.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
              Nothing here under the current filters.
            </div>
          )}
          {!byCity && data.items.map((it) => <TDrillRow key={it.id} it={it} onClose={onClose} />)}
          {byCity && groups && groups.map(([city, items]) => (
            <React.Fragment key={city}>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)',
                background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
                position: 'sticky', top: 0 }}>
                {city} · {items.length}
              </div>
              {items.map((it) => <TDrillRow key={it.id} it={it} onClose={onClose} />)}
            </React.Fragment>
          ))}
        </div>
      )}
    </window.Drawer>
  );
};

// Per-row "Research rev": one web_search fill through the existing
// field-fill system (migration 102 seeds the company/revenue_est
// definition). The cycling status mirrors what the fill actually does —
// one 3-8s web-search call — so waiting doesn't read as a hang.
const T_RESEARCH_STEPS = ['Searching the web…'];

const TResearchRev = ({ companyId }) => {
  const [state, setState] = React.useState('idle'); // idle | busy | done | none | error
  const [value, setValue] = React.useState(null);
  const run = (e) => {
    e.stopPropagation();
    setState('busy');
    window.AtlasApi.fillField('company', companyId, 'revenue_est')
      .then((r) => {
        const n = r && r.value != null ? Number(r.value) : null;
        if (n != null && !Number.isNaN(n) && n > 0) { setValue(n); setState('done'); }
        else setState('none');
      })
      .catch(() => setState('error'));
  };
  if (state === 'done') return <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{window.fmtMoney(value)} ✓</span>;
  if (state === 'none') return <span style={{ color: 'var(--text-4)' }}>no credible figure found</span>;
  if (state === 'error') return <span style={{ color: 'var(--rose-fg)' }}>research failed — retry from the company page</span>;
  if (state === 'busy') {
    return (
      <span style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px solid var(--accent)',
          borderTopColor: 'transparent', display: 'inline-block', animation: 'atlas-spin 0.9s linear infinite' }} />
        {T_RESEARCH_STEPS[0]}
      </span>
    );
  }
  return (
    <button onClick={run} title="Web-search this company's annual revenue (logged to the Activity feed)"
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 11.5, color: 'var(--accent-text)' }}>
      research rev ✨
    </button>
  );
};

const TDrillRow = ({ it, onClose }) => (
  <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span onClick={(e) => { if (!(e.metaKey || e.ctrlKey)) onClose(); window.AtlasNav?.goToCompany?.(it.id); }}
        style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', cursor: 'pointer',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {it.name}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>
        {it.interaction_count > 0
          ? `${tFmtInt(it.interaction_count)} touch${it.interaction_count === 1 ? '' : 'es'}`
          : 'no touches'}
      </span>
    </div>
    <div style={{ display: 'flex', gap: 10, marginTop: 2, fontSize: 11.5, color: 'var(--text-3)' }}>
      {it.hq_city && <span>{it.hq_city}</span>}
      {it.revenue_est != null
        ? <span>{window.fmtMoney(it.revenue_est)}</span>
        : <TResearchRev companyId={it.id} />}
      {it.last_interaction_at && <span>last touch {window.fmtDate(it.last_interaction_at)}</span>}
      {it.contact_names && it.contact_names.length > 0 && (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {it.contact_names.join(', ')}{it.contacts_owned > it.contact_names.length ? ` +${it.contacts_owned - it.contact_names.length}` : ''}
        </span>
      )}
    </div>
  </div>
);

// Radar — "I'm traveling to Newnan, GA; what's around me?" City-centroid
// radius search (companies carry city/state only — no street addresses yet,
// so distances are between city centers; the dot-map + address enrichment is
// a later iteration). The radius chips are the "expand the ring" control.
const T_RADII = [10, 25, 50, 100, 200];

const T_NEARBY_EXPORT_COLS = [
  { key: 'name', label: 'Company' },
  { key: 'hq_city', label: 'City' },
  { key: 'hq_state', label: 'State' },
  { key: 'mi', label: 'Miles From Center' },
  { key: 'revenue_est', label: 'Revenue Estimate' },
  { key: 'ebitda_est', label: 'EBITDA Estimate' },
  { key: 'interaction_count', label: 'Interactions' },
  { key: 'last_interaction_at', label: 'Last Interaction' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'id', label: 'Company ID' },
];

const TerritoryNearby = ({ near, dealmakerId, filters, onChangeRadius, onPickCandidate, onCenter, onClose }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (!near) return;
    let on = true;
    setData(null); setErr('');
    window.AtlasApi.territoryNearby({
      dealmaker_id: dealmakerId, radius_mi: near.radius,
      limit: T_DRILL_LIMIT, ...filters,
      // Typed/picked city vs dropped pin — the server labels a pin with the
      // nearest gazetteer place but measures distances from the pin itself.
      ...(near.q ? { center: near.q } : { center_lat: near.lat, center_lng: near.lng }),
    })
      .then((d) => {
        if (!on) return;
        setData(d);
        if (d && d.center && onCenter) onCenter(d.center);
      })
      .catch((e) => { if (on) setErr((e && (e.message || e.detail)) || 'Failed to load'); });
    return () => { on = false; };
  }, [near, dealmakerId, filters]);
  // Group the (already distance-sorted) rows under city headers.
  const groups = React.useMemo(() => {
    if (!data || !data.items) return null;
    const m = new Map();
    data.items.forEach((it) => {
      const k = `${(it.hq_city || '').trim()}|${it.mi}`;
      if (!m.has(k)) m.set(k, { city: (it.hq_city || '').trim(), mi: it.mi, items: [] });
      m.get(k).items.push(it);
    });
    return [...m.values()];
  }, [data]);
  if (!near) return null;
  const center = data?.center;
  const title = center
    ? `Near ${center.city}${center.state ? `, ${center.state}` : ''}${center.pin ? ' · dropped pin' : ''}`
    : (near.q ? `Near ${near.q}` : 'Around dropped pin');

  const doExport = (fmt) => {
    const slug = (near.q || `pin-${near.lat?.toFixed(3)}-${near.lng?.toFixed(3)}`)
      .toLowerCase().replace(/[^a-z0-9.-]+/g, '-').replace(/^-|-$/g, '');
    const base = `nearby-${slug}-${near.radius}mi`;
    const rows = data.items.map((it) => ({ ...it, contacts: (it.contact_names || []).join('; ') }));
    if (fmt === 'json') window.exportJson(`${base}.json`, rows);
    else window.exportCsv(`${base}.csv`, T_NEARBY_EXPORT_COLS, rows);
  };
  return (
    <window.Drawer open onClose={onClose} width={560} modeless
      title={title}
      subtitle={data && data.items
        ? `${tFmtInt(data.total_companies)} companies within ${near.radius} mi · drag the pin on the map to move the search`
        : 'City-radius search'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Radius</span>
        <div style={{ display: 'inline-flex', padding: 2, background: 'var(--surface-2)',
          border: '1px solid var(--border)', borderRadius: 7 }}>
          {T_RADII.map((r) => (
            <button key={r} onClick={() => onChangeRadius(r)} style={{
              padding: '3px 9px', border: 'none', cursor: 'pointer', borderRadius: 5, fontSize: 11.5,
              fontWeight: near.radius === r ? 600 : 500,
              background: near.radius === r ? 'var(--surface)' : 'transparent',
              color: near.radius === r ? 'var(--text)' : 'var(--text-3)',
              boxShadow: near.radius === r ? 'var(--shadow-sm)' : 'none',
            }}>{r} mi</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <window.ExportMenu disabled={!data || !data.items || data.items.length === 0}
          viewCount={data && data.items ? data.items.length : null}
          capNote={data && data.items && data.total_companies > data.items.length
            ? `closest ${data.items.length} of ${tFmtInt(data.total_companies)}` : null}
          onExport={doExport} />
      </div>
      {err && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!data && !err && <div style={{ padding: 14 }}><window.SkeletonRows rows={5} /></div>}
      {data && data.candidates && (
        <div style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 8 }}>
            Which {near.q.split(',')[0].trim()}?
          </div>
          {data.candidates.map((cand) => (
            <button key={`${cand.city}|${cand.state}`}
              onClick={() => onPickCandidate(cand)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                marginBottom: 4, border: '1px solid var(--border)', borderRadius: 7,
                background: 'var(--surface)', cursor: 'pointer', fontSize: 12.5,
                color: 'var(--accent-text)' }}>
              {cand.city}, {cand.state}
            </button>
          ))}
        </div>
      )}
      {data && data.items && (
        <div style={{ padding: '6px 0' }}>
          {data.items.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
              Nothing within {near.radius} mi under the current filters — widen the radius above.
            </div>
          )}
          {groups && groups.map((g) => (
            <React.Fragment key={`${g.city}|${g.mi}`}>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)',
                background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
                position: 'sticky', top: 0, display: 'flex', justifyContent: 'space-between' }}>
                <span>{g.city || '(no city)'} · {g.items.length}</span>
                <span style={{ fontWeight: 500 }}>{g.mi === 0 ? 'center' : `${g.mi} mi`}</span>
              </div>
              {g.items.map((it) => <TDrillRow key={it.id} it={it} onClose={onClose} />)}
            </React.Fragment>
          ))}
          {data.unplaced && data.unplaced.companies > 0 && (
            <div style={{ padding: '10px 14px', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-3)',
              background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
              {tFmtInt(data.unplaced.companies)} more compan{data.unplaced.companies === 1 ? 'y' : 'ies'} in
              nearby states have city names we couldn't place
              {data.unplaced.cities.length > 0 ? ` (${data.unplaced.cities.slice(0, 5).join(', ')})` : ''} —
              they may or may not be in range.
            </div>
          )}
        </div>
      )}
    </window.Drawer>
  );
};

const TerritoryMap = ({ activeUser }) => {
  const [members, setMembers] = React.useState([]);
  const [dealmakerId, setDealmakerId] = React.useState(activeUser?.id || '');
  const [minTouches, setMinTouches] = React.useState('1');
  const [minEbitda, setMinEbitda] = React.useState('');
  const [engagement, setEngagement] = React.useState('any');
  const [companyType, setCompanyType] = React.useState('sellers');
  const [minRevenue, setMinRevenue] = React.useState('');
  const [maxRevenue, setMaxRevenue] = React.useState('');
  const [industryId, setIndustryId] = React.useState('');
  const [industries, setIndustries] = React.useState([]);
  React.useEffect(() => {
    window.AtlasApi.listIndustries()
      .then((r) => {
        const items = Array.isArray(r) ? r : (r?.items || []);
        setIndustries(items.filter((i) => i.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      })
      .catch(() => {});
  }, []);
  const [metric, setMetric] = React.useState('companies');
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [drill, setDrill] = React.useState(null);
  const [hover, setHover] = React.useState(null); // {code, x, y}
  // Radar: nearInput is the live textbox; `near` (query + radius) only
  // updates on a pick, so the drawer never opens on half-typed text.
  const [nearInput, setNearInput] = React.useState('');
  const [near, setNear] = React.useState(null); // {q, radius}
  const [nearSug, setNearSug] = React.useState(null);  // typeahead matches
  const [nearOpen, setNearOpen] = React.useState(false);
  React.useEffect(() => {
    const q = nearInput.trim();
    if (q.length < 2) { setNearSug(null); return; }
    const t = setTimeout(() => {
      window.AtlasApi.territoryGeocode({ q })
        .then((d) => setNearSug((d && d.items) || []))
        .catch(() => setNearSug(null));
    }, 200);
    return () => clearTimeout(t);
  }, [nearInput]);
  const pickNear = React.useCallback((s) => {
    setNearInput(`${s.city}, ${s.state}`);
    setNearOpen(false);
    setNear((prev) => ({ q: `${s.city}, ${s.state}`, radius: (prev && prev.radius) || 50 }));
  }, []);
  // The active radar's center (lat/lng from the server response) — drawn as
  // a pin + dashed ring on the map while the drawer is open. When the CENTER
  // changes (new search — typeahead pick, Enter, candidate pick, pin) the
  // camera flies there; radius changes re-fire onCenter with the same coords
  // and must NOT re-zoom under the user.
  const [nearCenter, setNearCenter] = React.useState(null);
  const nearCenterRef = React.useRef(null);
  const handleNearCenter = React.useCallback((c) => {
    const prev = nearCenterRef.current;
    nearCenterRef.current = c;
    setNearCenter(c);
    if (!prev || prev.lat !== c.lat || prev.lng !== c.lng) {
      zoomToCity(c.lng, c.lat, c.state);
    }
  }, []);
  const closeNear = React.useCallback(() => {
    setNear(null); setNearCenter(null); nearCenterRef.current = null;
  }, []);
  const MAP = window.US_MAP;

  // ── Zoom / pan (Zillow-style): the SVG viewBox is the camera. null = the
  // whole map; zooming past DOT_ZOOM swaps the story from state choropleth
  // to city dots (fetched lazily, cached per dealmaker+filters).
  const BASE_VB = { x: 0, y: 0, w: 959, h: 593 };
  const DOT_ZOOM = 2.5;
  const MAX_ZOOM = 40;
  const [vb, setVb] = React.useState(null);
  const zf = vb ? BASE_VB.w / vb.w : 1;
  const dotsOn = zf >= DOT_ZOOM;
  const svgRef = React.useRef(null);
  const dragRef = React.useRef(null);       // {sx, sy, vb, moved}
  const clickGuardRef = React.useRef(false); // suppress click after a drag
  const pinDragRef = React.useRef(false);    // radar pin being dragged
  const [cityDots, setCityDots] = React.useState(null);
  const cityDotsKeyRef = React.useRef('');
  const clampVb = (v) => {
    const w = Math.min(BASE_VB.w, Math.max(BASE_VB.w / MAX_ZOOM, v.w));
    const h = w * (BASE_VB.h / BASE_VB.w);
    const pad = w * 0.25;
    return {
      x: Math.min(BASE_VB.w - w + pad, Math.max(-pad, v.x)),
      y: Math.min(BASE_VB.h - h + pad, Math.max(-pad, v.y)),
      w, h,
    };
  };
  // The svg now has a FIXED height (fit the viewport, per Andres — the map
  // was taller than the screen), so preserveAspectRatio letterboxes it.
  // Every client↔map conversion must therefore use the CONTENT rect (uniform
  // scale + centering offsets), not the raw bounding rect. vbRef keeps the
  // stable wheel listener reading the live camera.
  const vbRef = React.useRef(vb);
  vbRef.current = vb;
  const mapFrame = () => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const cur = vbRef.current || BASE_VB;
    const scale = Math.min(rect.width / cur.w, rect.height / cur.h);
    return {
      cur, scale,
      ox: rect.left + (rect.width - cur.w * scale) / 2,
      oy: rect.top + (rect.height - cur.h * scale) / 2,
    };
  };
  const zoomAt = React.useCallback((clientX, clientY, factor) => {
    const f = mapFrame();
    if (!f) return;
    const px = f.cur.x + (clientX - f.ox) / f.scale;
    const py = f.cur.y + (clientY - f.oy) / f.scale;
    setVb((prev) => {
      const cur = prev || BASE_VB;
      const w = Math.min(BASE_VB.w, Math.max(BASE_VB.w / MAX_ZOOM, cur.w / factor));
      if (w >= BASE_VB.w) return null;
      const h = w * (BASE_VB.h / BASE_VB.w);
      return clampVb({
        x: px - ((px - cur.x) / cur.w) * w,
        y: py - ((py - cur.y) / cur.h) * h,
        w, h,
      });
    });
  }, []);
  const zoomCenter = (factor) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    zoomAt(r.left + r.width / 2, r.top + r.height / 2, factor);
  };
  // Wheel must be a NATIVE non-passive listener — React's synthetic onWheel
  // is passive, so preventDefault (needed to stop page scroll) is ignored.
  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.3 : 1 / 1.3);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [zoomAt, data != null]);
  const onMapPointerDown = (e) => {
    if (!vb) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, vb, moved: false };
  };
  const onMapPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) <= 4) return;
    if (!d.moved && svgRef.current && svgRef.current.setPointerCapture) {
      // Capture only once this is definitely a drag — capturing on pointerdown
      // would retarget the tail click to the svg and swallow dot/state clicks.
      try { svgRef.current.setPointerCapture(e.pointerId); } catch { /* stale pointer */ }
    }
    d.moved = true;
    const f = mapFrame();
    if (!f) return;
    setVb(clampVb({ ...d.vb, x: d.vb.x - dx / f.scale, y: d.vb.y - dy / f.scale }));
  };
  const onMapPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.moved) {
      // The click event lands right after pointerup — swallow that one.
      clickGuardRef.current = true;
      setTimeout(() => { clickGuardRef.current = false; }, 0);
    }
  };
  // Client pixel → map (viewBox) coordinates, for drop-a-pin clicks.
  const clientToMap = (e) => {
    const f = mapFrame();
    if (!f) return null;
    return [f.cur.x + (e.clientX - f.ox) / f.scale,
            f.cur.y + (e.clientY - f.oy) / f.scale];
  };
  // Drop-a-pin: zoomed-in clicks on the map (not a dot) search around that
  // exact point via the inverse projection.
  const dropPin = (e) => {
    const pt = clientToMap(e);
    const ll = pt && window.GEO_UNPROJECT && window.GEO_UNPROJECT(pt[0], pt[1]);
    if (!ll) return;
    setNearInput('');
    setNear((prev) => ({ lat: ll[1], lng: ll[0], radius: (prev && prev.radius) || 25 }));
  };
  // County borders: pre-projected path strings (us_counties.json, built by
  // backend/scripts/build_county_lines.py) — fetched once on first zoom,
  // never bundled (~630KB).
  const [countyPaths, setCountyPaths] = React.useState(null);
  const countyReqRef = React.useRef(false);
  React.useEffect(() => {
    if (!dotsOn || countyReqRef.current) return;
    countyReqRef.current = true;
    fetch('us_counties.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setCountyPaths(d); })
      .catch(() => {});
  }, [dotsOn]);
  // State → city cascade: picking a state zooms the map to it; picking a
  // city opens the radar around it. City options come from the same
  // /territory/cities payload as the dots.
  const [stateSel, setStateSel] = React.useState('');
  const zoomToState = (code) => {
    const el = svgRef.current && svgRef.current.querySelector(`path[data-code="${code}"]`);
    if (!el) return;
    const bb = el.getBBox();
    const ratio = BASE_VB.h / BASE_VB.w;
    const w = Math.max(bb.width * 1.3, (bb.height * 1.3) / ratio, BASE_VB.w / MAX_ZOOM);
    const h = w * ratio;
    setVb(clampVb({ x: bb.x + bb.width / 2 - w / 2, y: bb.y + bb.height / 2 - h / 2, w, h }));
  };
  const changeStateSel = (code) => {
    setStateSel(code);
    if (code) zoomToState(code); else setVb(null);
  };
  // Zoom the camera onto one city (dot click / cascade pick) so the drawer
  // opens WITH the neighborhood visible behind it — "show me everything
  // there". Only zooms IN; if you're already deeper, stay put.
  const zoomToCity = React.useCallback((lng, lat, code) => {
    const [cx, cy] = window.GEO_PROJECT(lng, lat, code);
    setVb((prev) => {
      const w = Math.min((prev || BASE_VB).w, BASE_VB.w / 14);
      const h = w * (BASE_VB.h / BASE_VB.w);
      return clampVb({ x: cx - w / 2, y: cy - h / 2, w, h });
    });
  }, []);

  React.useEffect(() => {
    let on = true;
    window.AtlasApi.listTeamMembers()
      .then((m) => {
        if (!on) return;
        const items = (Array.isArray(m) ? m : (m.items || []))
          .filter((u) => u.active !== false)
          .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        setMembers(items);
      })
      .catch(() => {});
    return () => { on = false; };
  }, []);

  // Same shape the API takes; also handed to the drill so the drawer returns
  // exactly the rows the map counted.
  const filters = React.useMemo(() => ({
    min_interactions: Number(minTouches) || 0,
    ...(minEbitda ? { min_ebitda: Number(minEbitda) } : {}),
    ...(minRevenue ? { min_revenue: Number(minRevenue) } : {}),
    ...(maxRevenue ? { max_revenue: Number(maxRevenue) } : {}),
    ...(industryId ? { industry_id: industryId } : {}),
    company_type: companyType,
    engagement,
  }), [minTouches, minEbitda, minRevenue, maxRevenue, industryId, companyType, engagement]);

  React.useEffect(() => {
    if (!dealmakerId) return;
    let on = true;
    setData(null); setErr(null);
    window.AtlasApi.territoryStates({ dealmaker_id: dealmakerId, ...filters })
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr(e?.message || 'Failed to load'); });
    return () => { on = false; };
  }, [dealmakerId, filters]);

  // Lazy dot data: the first zoom past the threshold (or a state pick, which
  // needs the city list) fetches; a dealmaker or filter change invalidates
  // the cache key so the next zoom refetches.
  React.useEffect(() => {
    if ((!dotsOn && !stateSel) || !dealmakerId) return;
    const key = `${dealmakerId}|${JSON.stringify(filters)}`;
    if (cityDotsKeyRef.current === key && cityDots) return;
    cityDotsKeyRef.current = key;
    let on = true;
    setCityDots(null);
    window.AtlasApi.territoryCities({ dealmaker_id: dealmakerId, ...filters })
      .then((d) => { if (on && cityDotsKeyRef.current === key) setCityDots(d); })
      .catch(() => {});
    return () => { on = false; };
  }, [dotsOn, stateSel, dealmakerId, filters]);

  const byCode = React.useMemo(() => {
    const m = {};
    (data?.states || []).forEach((s) => { if (s.code) m[s.code] = s; });
    return m;
  }, [data]);
  const max = React.useMemo(
    () => Math.max(0, ...(data?.states || []).map((s) => s[metric] || 0)),
    [data, metric],
  );
  const mapped = (data?.states || []).reduce((a, s) => a + s.companies, 0);
  const canadaN = (data?.canada || []).reduce((a, s) => a + s.companies, 0);
  const otherN = (data?.other || []).reduce((a, s) => a + s.companies, 0);
  const noState = data?.no_state?.companies || 0;
  const topStates = (data?.states || []).slice()
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0)).slice(0, 12);

  // Zillow-style live panel: once dots are on, the right side describes THE
  // VIEWPORT — recomputed every pan/zoom from the already-loaded city data
  // (pure client math, no refetch).
  const inView = React.useMemo(() => {
    if (!dotsOn || !cityDots) return null;
    const view = vb || BASE_VB;
    const cities = [];
    for (const c of cityDots.cities) {
      const [x, y] = window.GEO_PROJECT(c.lng, c.lat, c.code);
      if (x >= view.x && x <= view.x + view.w && y >= view.y && y <= view.y + view.h) {
        cities.push(c);
      }
    }
    return {
      count: cities.length,
      companies: cities.reduce((a, c) => a + c.companies, 0),
      revenue: cities.reduce((a, c) => a + (c.revenue || 0), 0),
      top: cities.sort((a, b) => (b[metric] || 0) - (a[metric] || 0)).slice(0, 12),
    };
  }, [dotsOn, cityDots, vb, metric]);

  const openDrill = (region, companies, topCities) => setDrill({ region, companies, topCities });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <window.Combobox searchable value={dealmakerId}
          onChange={(e) => setDealmakerId(e.target.value)}
          options={[
            { value: 'all', label: 'All dealmakers', hint: 'every company in Atlas' },
            ...members.map((m) => ({ value: m.id, label: m.full_name || m.email })),
          ]}
          placeholder="Pick a dealmaker" style={{ minWidth: 190 }} />
        <window.Combobox value={companyType} onChange={(e) => setCompanyType(e.target.value)}
          options={T_TYPE_OPTS} style={{ width: 148 }} />
        <window.Combobox searchable value={industryId}
          onChange={(e) => setIndustryId(e.target.value)}
          options={[
            { value: '', label: 'Industry: any' },
            ...industries.map((i) => ({ value: i.id, label: i.name })),
          ]}
          style={{ width: 160 }} />
        <window.Combobox value={minTouches} onChange={(e) => setMinTouches(e.target.value)}
          options={T_TOUCH_OPTS} style={{ width: 118 }} />
        <window.Combobox value={minRevenue} onChange={(e) => setMinRevenue(e.target.value)}
          options={T_REV_MIN_OPTS} style={{ width: 118 }} />
        <window.Combobox value={maxRevenue} onChange={(e) => setMaxRevenue(e.target.value)}
          options={T_REV_MAX_OPTS} style={{ width: 118 }} />
        <window.Combobox value={minEbitda} onChange={(e) => setMinEbitda(e.target.value)}
          options={T_EBITDA_OPTS} style={{ width: 138 }} />
        <window.Combobox value={engagement} onChange={(e) => setEngagement(e.target.value)}
          options={T_ENGAGEMENT_OPTS} style={{ width: 156 }} />
        {/* State → City cascade: pick a state to zoom the map to it, then a
            city to open the radar around it. City options ride the same
            /territory/cities payload as the dots. */}
        <window.Combobox searchable value={stateSel}
          onChange={(e) => changeStateSel(e.target.value)}
          options={[
            { value: '', label: 'State: any' },
            ...(data?.states || []).map((s) => ({
              value: s.code, label: s.region, hint: tFmtInt(s.companies),
            })),
          ]}
          style={{ width: 150 }} />
        {stateSel && (
          <window.Combobox searchable value=""
            onChange={(e) => {
              const c = (cityDots?.cities || []).find(
                (x) => x.code === stateSel && x.city === e.target.value);
              if (c) { zoomToCity(c.lng, c.lat, c.code); pickNear({ city: c.city, state: c.code }); }
            }}
            options={[
              { value: '', label: cityDots ? 'City…' : 'Loading cities…' },
              ...(cityDots?.cities || [])
                .filter((c) => c.code === stateSel)
                .map((c) => ({ value: c.city, label: c.city, hint: tFmtInt(c.companies) })),
            ]}
            placeholder="City…" style={{ width: 150 }} />
        )}
        {/* Near typeahead: suggestions from the gazetteer as you type — the
            bare Enter-only input read as broken (no affordance, no feedback).
            Picking a suggestion opens the radar drawer; Enter takes the top
            match. */}
        <div style={{ position: 'relative' }}>
          <input
            value={nearInput}
            onChange={(e) => { setNearInput(e.target.value); setNearOpen(true); }}
            onFocus={() => setNearOpen(true)}
            onBlur={() => setTimeout(() => setNearOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setNearOpen(false); return; }
              // keyCode fallback: automated/IME key events can arrive with an
              // empty e.key (see notes composer, #138).
              if (e.key === 'Enter' || e.keyCode === 13) {
                const top = (nearSug || [])[0];
                if (top) pickNear(top);
                else if (nearInput.trim()) {
                  setNear({ q: nearInput.trim(), radius: (near && near.radius) || 50 });
                  setNearOpen(false);
                }
              }
            }}
            placeholder={'Near… e.g. "Newnan, GA"'}
            title="Radius search: type a city and pick a match to see companies around it"
            style={{ width: 170, height: 28, padding: '0 10px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 7,
              background: 'var(--surface)', color: 'var(--text)', outline: 'none' }} />
          {nearOpen && nearSug && nearSug.length > 0 && (
            <div style={{ position: 'absolute', top: 30, left: 0, minWidth: 190, zIndex: 60,
              background: 'var(--surface)', border: '1px solid var(--border-strong)',
              borderRadius: 8, boxShadow: 'var(--shadow-md, var(--shadow-sm))', padding: 3,
              maxHeight: 240, overflowY: 'auto' }}>
              {nearSug.map((s) => (
                <button key={`${s.city}|${s.state}`}
                  onMouseDown={(e) => e.preventDefault() /* keep input focus so blur doesn't race the click */}
                  onClick={() => pickNear(s)}
                  style={{ display: 'block', width: '100%', textAlign: 'left',
                    padding: '5px 9px', border: 'none', borderRadius: 5, cursor: 'pointer',
                    background: 'transparent', fontSize: 12, color: 'var(--text)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  {s.city}, <span style={{ color: 'var(--text-3)' }}>{s.state}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'inline-flex', padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
          {T_METRICS.map((m) => (
            <button key={m.key} onClick={() => setMetric(m.key)} title={m.help} style={{
              padding: '4px 10px', border: 'none', cursor: 'pointer', borderRadius: 5, fontSize: 12,
              fontWeight: metric === m.key ? 600 : 500,
              background: metric === m.key ? 'var(--surface)' : 'transparent',
              color: metric === m.key ? 'var(--text)' : 'var(--text-3)',
              boxShadow: metric === m.key ? 'var(--shadow-sm)' : 'none',
            }}>{m.label}</button>
          ))}
        </div>
      </div>

      {err && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!data && !err && <window.SkeletonRows rows={8} />}

      {data && (
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 560px', minWidth: 380, position: 'relative',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
            background: 'var(--surface)', padding: 14 }}>
            <svg ref={svgRef}
              viewBox={vb ? `${vb.x} ${vb.y} ${vb.w} ${vb.h}` : MAP.viewBox}
              style={{ width: '100%', display: 'block',
                // Fit the viewport instead of growing with page width — the
                // full-width map was taller than the screen. mapFrame() owns
                // the letterbox math this fixed height introduces.
                height: 'clamp(360px, calc(100vh - 320px), 720px)',
                cursor: vb ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
                touchAction: 'none' }}
              role="img" aria-label="Company concentration by US state"
              onPointerDown={onMapPointerDown}
              onPointerMove={onMapPointerMove}
              onPointerUp={onMapPointerUp}
              onPointerLeave={onMapPointerUp}>
              {MAP.separators.map((d, i) => (
                <path key={i} d={d} fill="none" stroke="var(--border-strong)" strokeWidth={1 / zf} />
              ))}
              {Object.entries(MAP.paths).map(([code, d]) => {
                const s = byCode[code];
                const v = s ? (s[metric] || 0) : 0;
                return (
                  <path key={code} d={d} data-code={code}
                    fill={tFill(v, max)}
                    fillOpacity={dotsOn ? 0.3 : 1}
                    stroke={hover?.code === code ? 'var(--accent)' : 'var(--border)'}
                    strokeWidth={(hover?.code === code ? 1.5 : 0.75) / zf}
                    style={{ cursor: s || dotsOn ? 'pointer' : 'default', transition: 'fill-opacity .15s' }}
                    onMouseMove={(e) => setHover({ code, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onClick={(e) => {
                      if (clickGuardRef.current) return;
                      // Zoomed in, a map click means "search around here" —
                      // drop a pin. Zoomed out it stays the state drill.
                      if (dotsOn) { dropPin(e); return; }
                      if (s) openDrill(s.region, s.companies, s.top_cities);
                    }}>
                  </path>
                );
              })}
              {/* County borders — pre-projected Census 20m outlines, only
                  when zoomed (they'd be noise at national scale). */}
              {dotsOn && countyPaths && ['l48', 'ak', 'hi'].map((p) => (
                countyPaths[p] ? (
                  <path key={`county-${p}`} d={countyPaths[p]} fill="none"
                    stroke="var(--border-strong)" strokeOpacity="0.6"
                    strokeWidth={1.1 / zf} pointerEvents="none" />
                ) : null
              ))}
              {/* City dots — the zoomed-in story. Radii shrink with zoom so
                  they stay screen-constant and size by the ACTIVE metric
                  (companies vs revenue concentration); click = zoom onto the
                  city + radar around it. */}
              {dotsOn && cityDots && (() => {
                const dotMax = Math.max(1, ...cityDots.cities.map((c) => c[metric] || 0));
                return cityDots.cities.map((c) => {
                  const [x, y] = window.GEO_PROJECT(c.lng, c.lat, c.code);
                  const v = c[metric] || 0;
                  if (metric === 'revenue' && v <= 0) return null;
                  const r = Math.min(16, 2.2 + Math.sqrt(v / dotMax) * 15) / zf;
                  return (
                    <circle key={`${c.code}|${c.city}`} cx={x} cy={y} r={r}
                      fill="var(--accent)" fillOpacity="0.8"
                      stroke="var(--surface)" strokeWidth={0.9 / zf}
                      style={{ cursor: 'pointer' }}
                      onMouseMove={(e) => { e.stopPropagation(); setHover({ dot: c, x: e.clientX, y: e.clientY }); }}
                      onMouseLeave={() => setHover(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (clickGuardRef.current) return;
                        zoomToCity(c.lng, c.lat, c.code);
                        setNearInput(`${c.city}, ${c.code}`);
                        setNear({ q: `${c.city}, ${c.code}`, radius: 25 });
                      }} />
                  );
                });
              })()}
              {/* City name labels — biggest clusters first, greedily skipping
                  any label whose box would overlap one already placed, capped
                  at 60 in view. Sizes divide by zf so text stays
                  screen-constant while zooming. pointer-events off so labels
                  never steal dot clicks. */}
              {dotsOn && cityDots && (() => {
                const view = vb || BASE_VB;
                const pts = [];
                for (const c of cityDots.cities) {
                  const [x, y] = window.GEO_PROJECT(c.lng, c.lat, c.code);
                  if (x < view.x || x > view.x + view.w || y < view.y || y > view.y + view.h) continue;
                  pts.push({ c, x, y });
                }
                pts.sort((a, b) => b.c.companies - a.c.companies);
                const placed = [];
                const labels = [];
                const fs = 10 / zf;
                for (const p of pts) {
                  if (labels.length >= 60) break;
                  const r = Math.min(16, 2.4 + Math.sqrt(p.c.companies) * 1.7) / zf;
                  const lx = p.x + r + 3 / zf;
                  const w = (p.c.city.length * 6 + 8) / zf, h = 13 / zf;
                  const box = { x0: p.x - r, x1: lx + w, y0: p.y - h / 2, y1: p.y + h / 2 };
                  if (placed.some((b) => !(box.x1 < b.x0 || box.x0 > b.x1 || box.y1 < b.y0 || box.y0 > b.y1))) continue;
                  placed.push(box);
                  labels.push(
                    <text key={`l|${p.c.code}|${p.c.city}`} x={lx} y={p.y}
                      fontSize={fs} fontWeight="600" dominantBaseline="central"
                      fill="var(--text-2)" stroke="var(--surface)" strokeWidth={2.5 / zf}
                      style={{ paintOrder: 'stroke', letterSpacing: '0.01em' }}>
                      {p.c.city}
                    </text>,
                  );
                }
                return <g style={{ pointerEvents: 'none' }}>{labels}</g>;
              })()}
              {/* Active radar ring: pin + dashed circle at the search center,
                  radius in real miles (projected via a point radius-north).
                  The pin is DRAGGABLE (Zillow-style): the ring follows the
                  pointer live, and releasing re-runs the search at the new
                  spot. nearCenterRef is pre-seeded with the drop coords so
                  handleNearCenter's fly-to sees "same center" and doesn't
                  yank the camera mid-exploration. */}
              {near && nearCenter && (() => {
                const [cx, cy] = window.GEO_PROJECT(nearCenter.lng, nearCenter.lat, nearCenter.state);
                const [, ey] = window.GEO_PROJECT(nearCenter.lng, nearCenter.lat + near.radius / 69, nearCenter.state);
                const rr = Math.abs(ey - cy);
                return (
                  <g>
                    <circle cx={cx} cy={cy} r={rr} fill="var(--accent)" fillOpacity="0.05"
                      stroke="var(--accent)" strokeWidth={1.2 / zf}
                      strokeDasharray={`${6 / zf} ${4 / zf}`} pointerEvents="none" />
                    <circle cx={cx} cy={cy} r={3.5 / zf} fill="var(--accent)"
                      stroke="var(--surface)" strokeWidth={1 / zf} pointerEvents="none" />
                    <circle cx={cx} cy={cy} r={22 / zf} fill="transparent"
                      style={{ cursor: pinDragRef.current ? 'grabbing' : 'grab' }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        pinDragRef.current = true;
                        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* stale pointer */ }
                      }}
                      onPointerMove={(e) => {
                        if (!pinDragRef.current) return;
                        e.stopPropagation();
                        const pt = clientToMap(e);
                        const ll = pt && window.GEO_UNPROJECT && window.GEO_UNPROJECT(pt[0], pt[1]);
                        if (!ll) return;
                        nearCenterRef.current = { ...nearCenterRef.current, lat: ll[1], lng: ll[0] };
                        setNearCenter((p) => ({ ...p, lat: ll[1], lng: ll[0] }));
                      }}
                      onPointerUp={(e) => {
                        if (!pinDragRef.current) return;
                        e.stopPropagation();
                        pinDragRef.current = false;
                        const c = nearCenterRef.current;
                        setNearInput('');
                        setNear((prev) => ({ lat: c.lat, lng: c.lng, radius: (prev && prev.radius) || 25 }));
                      }} />
                  </g>
                );
              })()}
            </svg>
            {/* Zoom controls */}
            <div style={{ position: 'absolute', top: 22, right: 22, display: 'flex',
              flexDirection: 'column', gap: 4, zIndex: 2 }}>
              {[
                ['+', 'Zoom in', () => zoomCenter(1.6)],
                ['−', 'Zoom out', () => zoomCenter(1 / 1.6)],
                ['⌂', 'Reset view', () => setVb(null)],
              ].map(([glyph, label, fn]) => (
                <button key={label} onClick={fn} title={label} style={{
                  width: 26, height: 26, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', border: '1px solid var(--border-strong)',
                  borderRadius: 6, background: 'var(--surface)', color: 'var(--text-2)',
                  cursor: 'pointer', fontSize: 14, lineHeight: 1, boxShadow: 'var(--shadow-sm)',
                }}>{glyph}</button>
              ))}
            </div>
            {dotsOn && !cityDots && (
              <div style={{ position: 'absolute', top: 22, left: 22, zIndex: 2,
                fontSize: 11.5, color: 'var(--text-3)', background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px' }}>
                Loading cities…
              </div>
            )}
            {hover && (
              <div style={{ position: 'fixed', left: Math.min(hover.x + 12, window.innerWidth - 230),
                top: hover.y + 12, zIndex: 60, pointerEvents: 'none',
                background: 'var(--surface)', border: '1px solid var(--border-strong)',
                borderRadius: 8, boxShadow: 'var(--shadow-sm)', padding: '7px 10px',
                fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-2)', maxWidth: 220 }}>
                {hover.dot ? (
                  <>
                    <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>
                      {hover.dot.city}, {hover.dot.code}
                    </div>
                    <div>{tFmtInt(hover.dot.companies)} companies · {tFmtInt(hover.dot.interactions)} interactions
                      {hover.dot.revenue > 0 ? ` · ${window.fmtMoney(hover.dot.revenue)} rev` : ''}</div>
                    <div style={{ color: 'var(--text-4)' }}>click for what's around here</div>
                  </>
                ) : (
                <>
                <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>{MAP.names[hover.code]}</div>
                {byCode[hover.code] ? (
                  <>
                    <div>{tFmtInt(byCode[hover.code].companies)} companies · {tFmtInt(byCode[hover.code].interactions)} interactions
                      {byCode[hover.code].revenue > 0 ? ` · ${window.fmtMoney(byCode[hover.code].revenue)} rev` : ''}</div>
                    {(byCode[hover.code].top_cities || []).length > 0 && (
                      <div>{byCode[hover.code].top_cities.slice(0, 3).map((c) => `${c.city} ${c.n}`).join(' · ')}</div>
                    )}
                    {byCode[hover.code].last_interaction_at && (
                      <div style={{ color: 'var(--text-4)' }}>last touch {window.fmtDate(byCode[hover.code].last_interaction_at)}</div>
                    )}
                  </>
                ) : <div style={{ color: 'var(--text-4)' }}>Nothing here</div>}
                </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
              fontSize: 11, color: 'var(--text-3)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--surface-2)',
                border: '1px solid var(--border)' }} />
              <span>0</span>
              <span style={{ width: 120, height: 8, borderRadius: 4, border: '1px solid var(--border)',
                background: 'linear-gradient(90deg, color-mix(in oklab, var(--accent) 12%, var(--surface)), color-mix(in oklab, var(--accent) 90%, var(--surface)))' }} />
              <span>{tFmtMetric(metric, max)}{metric === 'revenue' ? ' rev' : ` ${metric}`}</span>
              <span style={{ marginLeft: 'auto' }}>
                {dotsOn
                  ? 'each dot is a city — click one to see what\'s around it'
                  : `${tFmtInt(mapped)} companies on the map · click a state for the list · scroll to zoom into city dots`}
              </span>
            </div>
          </div>

          <div style={{ flex: '0 1 300px', minWidth: 240 }}>
            {/* Zoomed in: the panel describes the VIEWPORT and re-ranks live
                as you pan/zoom (Zillow-style). Zoomed out: top states. */}
            {inView ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
                  color: 'var(--text-4)', marginBottom: 4 }}>In view</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>
                  {tFmtInt(inView.companies)} companies · {window.fmtMoney(inView.revenue)} rev
                  · {tFmtInt(inView.count)} cities
                </div>
                {inView.count === 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                    Nothing in this view — pan or zoom out.
                  </div>
                )}
                {inView.top.map((c) => (
                  <div key={`${c.code}|${c.city}`}
                    onClick={() => {
                      zoomToCity(c.lng, c.lat, c.code);
                      setNearInput(`${c.city}, ${c.code}`);
                      setNear((p) => ({ q: `${c.city}, ${c.code}`, radius: (p && p.radius) || 25 }));
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                      borderRadius: 6, cursor: 'pointer', fontSize: 12.5 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ flex: 1, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.city}, <span style={{ color: 'var(--text-4)' }}>{c.code}</span>
                    </span>
                    <span style={{ color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{tFmtMetric(metric, c[metric])}</span>
                  </div>
                ))}
              </>
            ) : (
              <>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
              color: 'var(--text-4)', marginBottom: 6 }}>Top states</div>
            {topStates.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                Nothing mapped under the current filters.
              </div>
            )}
            {topStates.map((s) => (
              <div key={s.region} onClick={() => openDrill(s.region, s.companies, s.top_cities)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                  borderRadius: 6, cursor: 'pointer', fontSize: 12.5 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0,
                  background: tFill(s[metric] || 0, max), border: '1px solid var(--border)' }} />
                <span style={{ flex: 1, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.region}</span>
                <span style={{ color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{tFmtMetric(metric, s[metric])}</span>
              </div>
            ))}
              </>
            )}
            {!inView && (noState > 0 || canadaN > 0 || otherN > 0) && (
              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)',
                fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
                {noState > 0 && (
                  <div>
                    <span onClick={() => openDrill('none', noState)}
                      style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>
                      {tFmtInt(noState)} companies have no state
                    </span> — not on the map
                  </div>
                )}
                {(data.canada || []).map((s) => (
                  <div key={s.region}>
                    <span onClick={() => openDrill(s.region, s.companies, s.top_cities)}
                      style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>{s.region}</span>
                    {' '}(Canada) · {tFmtInt(s.companies)}
                  </div>
                ))}
                {otherN > 0 && (
                  <div>{tFmtInt(otherN)} elsewhere ({(data.other || []).slice(0, 3).map((s) => s.region).join(', ')})</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <TerritoryDrill drill={drill} dealmakerId={dealmakerId} filters={filters}
        dealmakerName={dealmakerId === 'all' ? 'All dealmakers'
          : (members.find((m) => m.id === dealmakerId) || {}).full_name}
        activeUserName={activeUser?.full_name}
        onClose={() => setDrill(null)} />
      <TerritoryNearby near={near} dealmakerId={dealmakerId} filters={filters}
        onChangeRadius={(r) => setNear((p) => ({ ...p, radius: r }))}
        onPickCandidate={(cand) => {
          setNearInput(`${cand.city}, ${cand.state}`);
          setNear((p) => ({ q: `${cand.city}, ${cand.state}`, radius: (p && p.radius) || 50 }));
        }}
        onCenter={handleNearCenter}
        onClose={closeNear} />
    </div>
  );
};

window.TerritoryMap = TerritoryMap;
